import { HOSTS, ENDPOINTS } from "@/constants";
import { httpClient } from "@/services/httpClient";
import type { Client, ClientsPlaces, Place, Project, RootState } from "@/types";
import type { RouterState } from "@/types/store";
import type { ActionTree } from "vuex";
import {
  LOAD_CLIENTS_PLACES,
  LOAD_CLIENTS_PLACES_FAILURE,
  LOAD_CLIENTS_PLACES_SUCCESS,
  LOAD_CLIENTS_SUCCESS,
  LOAD_PLACES_SUCCESS,
  LOAD_PROJECTS_SUCCESS,
  LOAD_AGGREGATIONS,
  LOAD_AGGREGATIONS_SUCCESS,
  LOAD_AGGREGATIONS_FAILURE,
  LOAD_UNITS,
  LOAD_UNITS_SUCCESS,
  LOAD_UNITS_FAILURE,
  SET_VIEW_MODE,
} from "./mutations";

export const actions: ActionTree<RouterState, RootState> = {
  setViewMode({ commit }, viewMode: any) {
    commit(SET_VIEW_MODE, viewMode);
  },

  fetchClientPlaces({ commit }) {
    commit(LOAD_CLIENTS_PLACES);
    Promise.all([
      httpClient.get(`${HOSTS.AWS}/${ENDPOINTS.CLIENTS}`),
      httpClient.get(`${HOSTS.AWS}/${ENDPOINTS.PLACES}`),
      httpClient.get(`${HOSTS.AWS}/${ENDPOINTS.PROJECTS}`),
    ]).then(
      (response: any[]) => {
        const excludedClientNames = ["ELEVEURS DE LA CHAMPAGNE", "SEAVALUE"];
        const excludedProjectNames = [
          "ELEVEURS DE LA CHAMPAGNE - Caurel",
          "SEA VALUE Lignol",
        ];

        // Filter out the clients with the excluded names
        const filteredClients = response[0].data.filter(
          (client: Client) => !excludedClientNames.includes(client.name),
        );

        // Map the filtered clients
        const clientsPlaces: ClientsPlaces[] = filteredClients.map(
          (client: Client) => ({
            ...client,
            value: client.id,
            label: client.name,
            children: [] as any,
          }),
        );

        // Filter out the places that belong to excluded clients
        const filteredPlaces = response[1].data.filter(
          (place: Place) => !excludedClientNames.includes(place.client.name),
        );

        // Filter out the projects with the excluded names
        const filteredProjects = response[2].data.filter(
          (project: Project) => !excludedProjectNames.includes(project.name),
        );

        filteredPlaces.forEach((place: Place) => {
          const indexPlaceInClients: number = clientsPlaces.findIndex(
            (client: ClientsPlaces) => client.id === place.client.id,
          );
          if (indexPlaceInClients !== -1) {
            const projects: Project[] = filteredProjects.filter(
              (project: any) => place.id === project.place.id,
            );
            clientsPlaces[indexPlaceInClients].children = [
              ...clientsPlaces[indexPlaceInClients].children,
              {
                ...place,
                value: place.id,
                label: place.name,
                children:
                  projects.length > 0
                    ? projects.map((project: any) => ({
                        ...project,
                        value: project.id,
                        label: project.name,
                      }))
                    : [],
              } as Place,
            ];
          }
        });

        commit(LOAD_CLIENTS_PLACES_SUCCESS, clientsPlaces);
        commit(LOAD_CLIENTS_SUCCESS, filteredClients);
        commit(LOAD_PLACES_SUCCESS, filteredPlaces);
        commit(LOAD_PROJECTS_SUCCESS, filteredProjects);
      },
      (error) => {
        commit(LOAD_CLIENTS_PLACES_FAILURE, error.message);
      },
    );
  },

  //AGGREGATIONS
  fetchAggregations({ commit }) {
    commit(LOAD_AGGREGATIONS);
    httpClient.get(`${HOSTS.AWS}/${ENDPOINTS.AGGREGATIONS}`).then(
      (response: any) => {
        commit(LOAD_AGGREGATIONS_SUCCESS, response.data);
      },
      (error) => {
        commit(LOAD_AGGREGATIONS_FAILURE, error.message);
      },
    );
  },

  loadUnits({ commit }) {
    commit(LOAD_UNITS);
    httpClient.get(`${HOSTS.AWS}/${ENDPOINTS.UNIT}`).then(
      (response: any) => {
        commit(LOAD_UNITS_SUCCESS, response.data);
      },
      (error) => {
        commit(LOAD_UNITS_FAILURE, error.message);
      },
    );
  },
};
